/**
 * パスキーの画面（PasskeysPage.tsx）と Creator の再認証の部品（components/Reauth.tsx の useReauth）をつなぐ「React に依存しない判断」。
 * node --test で確かめる（passkeyReauth.test.ts）。2026-09-28 money/reauth-ui で切り出し。
 *
 * WHY 切り出すか: 「どの確認の方法を出すか」「戻ってきた証明がどの操作のものか」「最後の 1 本を消せる手段は何か」は、
 *   間違えると本人が詰む（パスワードを持たない人にパスワード欄だけを出す）か、Google 等への往復を無駄にさせる。
 *   描画から離して、条件ごとに固定しておく。
 *
 * サーバーの正: apps/server/src/modules/money/passkeys.ts（登録・削除）と lib/developerReauth.ts（{ hasPassword, providers }）。
 */
import { isDevOAuthProvider, type ReauthMethods } from '../../devOAuth.ts';

/**
 * 操作の名前（useReauth の action。「Google で確認」から戻ったとき reauth.pending.action と比べる）。
 * 削除は対象の ID を含める = 戻った後に「どのパスキーを消す途中だったか」を画面の state なしで復元できる
 * （プロバイダの画面へ移ると React の state は消える。控えは sessionStorage の PendingOAuth.action だけ）
 */
export const PASSKEY_ADD_ACTION = 'passkey:add';
export const passkeyRemoveAction = (id: string) => `passkey:del:${id}`;

/** 戻ってきた action が削除のものなら、そのパスキーの ID。違えば null */
export function removalIdFromAction(action: string | null | undefined): string | null {
  if (!action || !action.startsWith('passkey:del:')) return null;
  const id = action.slice('passkey:del:'.length);
  return id.length > 0 ? id : null;
}

/** この画面の操作の action か（戻りの失敗 reauth.failure を、この画面で出すかどうかの判断） */
export const isPasskeyAction = (action: string | null | undefined) => !!action && action.startsWith('passkey:');

/**
 * 確認の方法（ダイアログがパスワード欄か「Google で確認」かを出し分ける）を、画面の読み込み時に分かる情報から作る。
 *   1. GET /v1/developers/me/identities（Core・{ hasPassword, identities: [{ provider }] }）が読めたらそれ（サーバーの正と同じ中身）
 *   2. 読めない間 / 失敗 → セッションの developer.hasPassword（古いサーバーは undefined = 持っているとみなす）・プロバイダは空。
 *      この場合でも、送った後のサーバーの 400 / 401 の details が正しい方法を返すので、ダイアログを出し直せば正しくなる
 * WHY 先に知る必要があるか: 登録は WebAuthn の create の**前**に確認を済ませたい（create の後にパスワード違いが分かると、
 *   認証器に使われないパスキーが残る）。登録の口は credential が無いと再認証の判定まで進まない = 「送ってみて details を得る」ができない
 */
export function passkeyReauthMethods(
  identities: { hasPassword?: boolean; identities?: Array<{ provider: string }> } | null | undefined,
  sessionHasPassword: boolean | undefined,
  // ログイン用パスキーの件数（GET /v1/developers/me/login-passkeys・Core）。1 以上なら部品が「パスキーで確認」を出す（2026-09-28 Creator の依頼）。
  // WHY: この画面は方法を details からではなく先に組み立てる = ここで足さないと、ログイン用パスキーしか持たない人が「確認の方法が無い」になる
  loginPasskeyCount = 0
): ReauthMethods {
  const loginPasskey = loginPasskeyCount > 0 ? { loginPasskey: true } : {};
  if (identities && typeof identities.hasPassword === 'boolean') {
    const providers = (identities.identities ?? []).map((i) => i.provider).filter(isDevOAuthProvider);
    return { hasPassword: identities.hasPassword, providers: [...new Set(providers)], ...loginPasskey };
  }
  return { hasPassword: sessionHasPassword !== false, providers: [], ...loginPasskey };
}

/**
 * 削除の確認の選択肢。'passkey' = 既存のパスキーで確認（WebAuthn の get）・'reauth' = パスワードか OAuth のやり直し（部品のダイアログ）。
 * **最後の 1 本は 'passkey' だけ**（サーバーの 403 passkey_assertion_required・Quality 第 2 回 N1）: パスワードや Google 等だけで
 *   消させると「確認なしで足せる 0 本の状態」に戻せてしまう。画面でも先に隠す = Google 等への往復を無駄にさせない（正はサーバー）。
 * パスキーが使えない端末で最後の 1 本 → 空（この端末では消せない。説明を出す）
 */
export function removalChoices(passkeysSupported: boolean, activeCount: number): Array<'passkey' | 'reauth'> {
  const out: Array<'passkey' | 'reauth'> = [];
  if (passkeysSupported) out.push('passkey');
  if (activeCount > 1) out.push('reauth');
  return out;
}

/**
 * 削除の口が「確認が無い」ときに返す 403 passkey_confirmation_required を、部品が扱える「再認証を求めるエラー」の形に読み替える。
 * WHY: 削除の手段は 3 つ（パスキー・パスワード・OAuth）なのでサーバーのコードは passkey_confirmation_required のまま（passkeys.ts）。
 *   ただし details に { hasPassword, providers } が載る（2026-09-28 から Core の developerReauthMethods）。部品の run / requestReauth は
 *   code が reauthentication_required のときだけダイアログを出すので、details を保ったまま読み替える（何も消費していない 403 なので送り直しは安全）。
 *   画面の読み込み時に方法が分からなかったとき（identities が読めない）の保険。それ以外のエラーはそのまま返す
 */
export function asReauthRequired(err: unknown): unknown {
  const e = err as { code?: unknown; status?: unknown; details?: unknown; message?: unknown } | null;
  if (!e || e.code !== 'passkey_confirmation_required' || !e.details || typeof e.details !== 'object') return err;
  const d = e.details as { hasPassword?: unknown; providers?: unknown };
  if (typeof d.hasPassword !== 'boolean' || !Array.isArray(d.providers)) return err;
  return { status: 400, code: 'reauthentication_required', message: typeof e.message === 'string' ? e.message : '', details: e.details };
}
